import { useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';

import type { QuizSetBuildInfoRes } from '@/api/gen/_model/quizSetBuildInfoRes';
import type { QuizSetDetailsRes } from '@/api/gen/_model/quizSetDetailsRes';
import { useTemplateAiTutorGetter } from '@/shared/components/Chatbot';

import { BuildSchema, type BuildSchemaInputType } from '../form/schema';
import { type SelectionSchoolInfo } from '../type';
import { findSchoolInfoDefaultFormValue } from '../util';
import { buildDefaultFormValues } from '../util/buildDefaultValuesBuilder';

/**
 * 퀴즈 빌드 페이지의 전체 form data를 관리합니다.
 */
export const useBuildQuizsetForm = ({
  quizSetData,
  quizSetInfo,
  actionType,
  selectionSchoolInfo,
}: {
  quizSetInfo: QuizSetBuildInfoRes;
  actionType: 'CREATE' | 'UPDATE' | 'DUPLICATE';
  selectionSchoolInfo?: SelectionSchoolInfo;
  quizSetData?: QuizSetDetailsRes;
}) => {
  const templateAiTutorGetter = useTemplateAiTutorGetter();

  const aiTutor = quizSetData?.aiTutorId
    ? templateAiTutorGetter(quizSetData.aiTutorId)
    : undefined;

  const schoolInfo = findSchoolInfoDefaultFormValue({
    userSavedData: quizSetInfo.userBuildInfo.lastQuizSetCreationInfo,
    publishers: selectionSchoolInfo?.publishers['KR'] || [],
    originSetData: actionType !== 'CREATE' ? quizSetData : undefined,
    isInitialize:
      actionType === 'CREATE'
        ? false
        : quizSetInfo.userBuildInfo.countryCode !== quizSetData?.countryCode,
  });

  const { quizList, ...restDefaultValues } = useMemo(() => {
    return buildDefaultFormValues({
      updateData: quizSetData,
      actionType,
      schoolInfo,
      aiTutor,
    });
  }, [quizSetData, actionType, schoolInfo, aiTutor]);

  const form = useForm<BuildSchemaInputType>({
    resolver: zodResolver(BuildSchema),
    defaultValues: {
      ...restDefaultValues,
      // FIXME: 퀴즈 삭제 후 캐시 문제를 피하기 위해
      // 퀴즈리스트는 빈 배열로 초기화합니다.
      // 더욱 근본적인 해결책을 찾아야합니다.
      quizList: [],
    },
  });

  useEffect(() => {
    actionType !== 'CREATE' && form.setValue('quizList', quizList);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [actionType]);

  return form;
};
